import { useTranslation } from 'react-i18next'
import { Base } from '@itsrever/design-system'
import { useNavigate } from 'react-router'

export interface IntegrationCardProps {
  id: string
  name: string
  description: string
  imageSrc: string
  status?: string
  tags?: string[]
  category: string
  integration_url?: string
  isFeatured?: boolean
}

export const IntegrationCard = ({
  id,
  name,
  description,
  imageSrc,
  status = 'INTEGRATION_STATUS_INACTIVE',
  tags,
  category,
  isFeatured
}: IntegrationCardProps) => {
  const navigate = useNavigate()
  const { t } = useTranslation()

  const displayName = name.replace(/_/g, ' ')

  const isInstalled = status === 'INTEGRATION_STATUS_ACTIVE'
  const navigateToDetails = () => {
    // The details page looks the integration up by its real name, so the URL carries that
    // and not displayName: an underscore turned into a space finds no row, and the page
    // then renders no credential fields at all.
    navigate(`/account/integrations/details/${encodeURIComponent(name)}`, {
      state: {
        id,
        name: displayName,
        description,
        imageSrc,
        status,
        tags,
        category
      }
    })
  }

  const renderButton = () => (
    <Base.Button
      type="button"
      variant={isInstalled ? 'secondary' : 'primary'}
      onClick={navigateToDetails}
    >
      {isInstalled
        ? t('integrations_store.manage')
        : t('integrations_store.install')}
    </Base.Button>
  )

  return (
    <>
      <div className="flex h-fit max-h-[250px] min-h-[250px] flex-col content-start items-stretch hover:bg-neutral-100">
        {isFeatured ? (
          <Base.Card className="relative flex h-full flex-1 flex-col items-stretch overflow-hidden p-4 text-sm font-medium">
            <img
              loading="lazy"
              src={imageSrc}
              className="aspect-square w-[52px] shrink-0 rounded-lg object-contain"
              alt={`${displayName} logo`}
            />

            <div className="flex h-full min-w-[240px] flex-1 grow flex-col justify-between">
              <div className="text-xl leading-snug text-neutral-900">
                {displayName}
              </div>

              <div className="mt-1 flex w-full max-w-[262px] flex-col">
                <div className="text-sm font-normal leading-5 tracking-normal text-neutral-400">
                  {description}
                </div>

                {tags && tags.length > 0 && (
                  <div className="mt-2 flex items-center gap-2 self-start text-xs font-medium uppercase leading-none tracking-wide text-blue-950">
                    {tags.map((tag, index) => (
                      <div
                        key={index}
                        className="my-auto overflow-hidden rounded-full bg-blue-200 px-2 py-0.5"
                      >
                        {tag}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="mt-auto flex w-full flex-col items-start whitespace-nowrap pt-3 text-sm font-medium leading-none tracking-normal">
                {renderButton()}
              </div>
            </div>
          </Base.Card>
        ) : (
          <Base.Card className="relative flex h-full flex-1 flex-col items-stretch justify-between overflow-hidden p-4 text-sm font-medium">
            {isInstalled && (
              <div className="absolute right-2 top-2 flex items-center justify-center">
                <div className="absolute right-2 top-2 flex h-4 items-center justify-center rounded-full bg-[#c2f5da] px-2 py-0.5">
                  <span className="text-[11px] font-medium uppercase leading-3 tracking-tight text-[#0b4627]">
                    Installed
                  </span>
                </div>
              </div>
            )}
            <div className="flex size-11 items-center justify-center overflow-hidden rounded-lg bg-neutral-100 p-1.5">
              <img
                loading="lazy"
                src={imageSrc}
                alt={displayName}
                className="size-8 rounded-[20px] object-contain"
              />
            </div>

            <div className="mt-3 flex w-full flex-col justify-center">
              <div className="mb-2 text-xl leading-snug text-neutral-900">
                {displayName}
              </div>

              <div className="line-clamp-3 min-h-[40px] text-sm font-normal leading-5 tracking-normal text-gray-400">
                {description}
              </div>
            </div>

            <div className="mt-auto flex w-full flex-col items-start whitespace-nowrap pt-3 text-sm font-medium leading-none tracking-normal">
              {renderButton()}
            </div>
          </Base.Card>
        )}
      </div>
    </>
  )
}
